// hooks
import { useCallback, useEffect, useState, Fragment } from "react";
import useHttp from "hooks/use-http";
import { useNavigate } from "react-router-dom";

import { useForm, Controller } from "react-hook-form";

// material styles
import {
  Grid,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";

// styled component
import {
  StyledStack,
  StyledMenuItem,
  StyledGridIcons,
} from "./displayEducation.style";

// material icons
import { Delete, Edit, FilterAlt } from "@mui/icons-material";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";

// helper
import GenerateHeadCell from "helper/generate-head-cell";

// constants
import { PAGINATION_LIMIT } from "constants";

// request method
import {
  getEducationCategoryRequest,
  getEducationDataRequest,
} from "api/requests/educations";

// products head cells
const headCells = GenerateHeadCell("education_list");

function DisplayEducation() {
  const { request, loading, setLoading } = useHttp(true);

  const {
    register,
    control,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();

  // education data state
  const [educationData, setEducationData] = useState({
    data: [],
    count: 0,
  });

  // state for filter
  const [searchValue, setSearchValue] = useState("");
  const [categoryList, setCategoryList] = useState({
    data: [],
    count: 0,
  });
  const [selectedCategory, setSelectedCategory] = useState();

  // page state for pagination
  const [page, setPage] = useState(1);

  // handle date
  const dateHandler = (value) =>
    value
      .split(" ")[0]
      .split("-")
      .map((item) => item)
      .join("/");

  // get all education data
  const getEducationData = async () => {
    const categoryRes = await request({
      url: getEducationCategoryRequest,
      values: {
        skip: 0,
        limit: 100,
        search: null,
      },
      isMulty: true,
    });
    setLoading(false);

    if (categoryRes) {
      let transformedData = categoryRes.data?.data.map((items) => ({
        id: items.id,
        title: items.title,
      }));
      setCategoryList({
        data: transformedData,
        count: categoryRes?.data.length,
      });
    }

    const values = {
      skip: searchValue ? 0 : (page - 1) * PAGINATION_LIMIT,
      limit: searchValue ? 100 : PAGINATION_LIMIT,
      search: searchValue ?? null,
      category: selectedCategory ?? null,
    };
    !searchValue.length && delete values.search;
    !selectedCategory && delete values.category;

    const res = await request({
      url: getEducationDataRequest,
      values,
      // isMulty: true,
    });

    setLoading(false);

    if (res) {
      if (res.data.count < 1) {
        return setEducationData({
          count: res.data.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد" }],
        });
      }

      let transformedData = res.data.data.map((items) => ({
        id: items.id,
        title: items.title,
        category: items.category?.title ? items.category?.title : "ثبت نشده",
        date: dateHandler(items.created_at.jdate),
        edit: (
          <IconButton
            color="primary"
            onClick={() => {
              navigate("/education/edit/" + items.id);
            }}
          >
            <Tooltip title="ویرایش" placement="top">
              <Edit />
            </Tooltip>
          </IconButton>
        ),
      }));

      setEducationData({
        data: transformedData,
        count: res.data.count,
      });
    }
    setLoading(false);
  };
  const getArticlesDataWithoutFilter = async () => {
    setSearchValue("");
    setSelectedCategory("");
    const res = await request({
      url: getEducationDataRequest,
      values: {
        skip: (page - 1) * PAGINATION_LIMIT,
        limit: PAGINATION_LIMIT,
      },
      isMulty: true,
    });

    setLoading(false);

    if (res) {
      if (res.data.count < 1) {
        return setEducationData({
          count: res.data.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد" }],
        });
      }

      let transformedData = res.data.data.map((items) => ({
        id: items.id,
        title: items.title,
        category: items.category?.title ? items.category?.title : "ثبت نشده",
        date: dateHandler(items.created_at.jdate),
        edit: (
          <IconButton
            color="primary"
            onClick={() => {
              navigate("/education/edit/" + items.id);
            }}
          >
            <Tooltip title="ویرایش" placement="top">
              <Edit />
            </Tooltip>
          </IconButton>
        ),
      }));

      setEducationData({
        data: transformedData,
        count: res.data.count,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    getEducationData();
  }, [page]);
  return (
    <DrawerCM title="آموزش">
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <StyledStack>
            <Grid container>
              <Grid item sm={6} xs={12} p={0.5}>
                <TextField
                  label="جست و جو"
                  fullWidth
                  id="outlined-size-small"
                  size="small"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select_category"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select_category">سردسته</InputLabel>
                      <Select
                        id="category"
                        name="category"
                        label="سردسته"
                        labelId="select-category"
                        value={selectedCategory ?? ""}
                        {...register("category")}
                        error={Boolean(errors.category)}
                        helperText={errors.category?.message}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        {categoryList?.data?.map((item, index) => (
                          <StyledMenuItem key={index} value={item.id}>
                            {item.title}
                          </StyledMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <StyledGridIcons item sm={2} xs={12} p={0.5}>
                <IconButton color="primary" onClick={getEducationData}>
                  <Tooltip title="اعمال فیلتر" placement="top">
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
                <IconButton
                  color="error"
                  onClick={getArticlesDataWithoutFilter}
                >
                  <Tooltip title="حذف فیلتر" placement="top">
                    <Delete />
                  </Tooltip>
                </IconButton>
              </StyledGridIcons>
            </Grid>
          </StyledStack>
          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                title="لیست آموزش ها"
                rows={educationData.data}
                headCells={headCells}
                page={page}
                addEducation
                addEducationCategory
                colspan={educationData.count === 0 ? "11" : ""}
                fontSize="12px"
                cellFontSize="12px"
              />
            </Grid>
            {educationData.count < 1 ? (
              ""
            ) : (
              <PaginationCM
                handleChange={(_, val) => setPage(val)}
                page={page}
                count={educationData.count}
              />
            )}
          </Grid>
        </Fragment>
      )}
    </DrawerCM>
  );
}

export default DisplayEducation;
